import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/axios';
import VaccinationForm from './VaccinationForm';
import PromptSelector from './PromptSelector';
import TraitsForm from './TraitsForm';
import PreferencesForm from './PreferencesForm';

const GENDER_OPTIONS = [
  'Man', 'Woman', 'Non-binary', 'Transgender', 'Genderqueer',
  'Genderfluid', 'Agender', 'Two-Spirit', 'Other',
];
const PRONOUN_OPTIONS = ['He/Him', 'She/Her', 'They/Them', 'Ze/Zir', 'Custom'];
const ORIENTATION_OPTIONS = [
  'Straight', 'Gay', 'Lesbian', 'Bisexual', 'Pansexual',
  'Asexual', 'Queer', 'Questioning', 'Other',
];
const INTERESTED_IN_OPTIONS = ['Men', 'Women', 'Non-binary people', 'Everyone'];

const styles = {
  container: { padding: '20px' },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    minWidth: 44,
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: { fontSize: '20px', fontWeight: '600' },
  section: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid var(--border-color)',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '16px',
  },
  photosGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '8px',
  },
  photoCell: {
    aspectRatio: '3/4',
    borderRadius: 'var(--radius-sm)',
    overflow: 'hidden',
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border-color)',
    position: 'relative',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  photoImg: { width: '100%', height: '100%', objectFit: 'cover' },
  photoDelete: {
    position: 'absolute',
    top: '4px',
    right: '4px',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: 'rgba(0,0,0,0.6)',
    color: '#fff',
    border: 'none',
    fontSize: '14px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addPhoto: {
    fontSize: '24px',
    color: 'var(--text-muted)',
  },
  tabs: {
    display: 'flex',
    gap: '0',
    borderBottom: '1px solid var(--border-color)',
    marginBottom: '20px',
  },
  tab: {
    flex: 1,
    padding: '10px',
    textAlign: 'center',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    color: 'var(--text-muted)',
    borderBottom: '2px solid transparent',
    transition: 'all 200ms',
  },
  tabActive: {
    color: 'var(--accent)',
    borderBottomColor: 'var(--accent)',
  },
};

export default function ProfileEdit() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('info');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [hasChanges, setHasChanges] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [bio, setBio] = useState('');
  const [gender, setGender] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [orientation, setOrientation] = useState('');
  const [interestedIn, setInterestedIn] = useState([]);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || '');
      setBio(user.bio || '');
      setGender(user.gender || '');
      setPronouns(user.pronouns || '');
      setOrientation(user.orientation || '');
      setInterestedIn(user.interested_in || []);
      setPhotos(user.photos || []);
    }
  }, [user]);

  const toggleInterestedIn = (value) => {
    setInterestedIn((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.put('/users/profile', {
        display_name: displayName,
        bio,
        gender,
        pronouns,
        orientation,
        interested_in: interestedIn,
      });
      await refreshUser();
      setHasChanges(false);
      setSuccess('Profile saved');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('photo', file);

    try {
      await api.post('/users/photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Photo upload failed');
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await api.delete(`/users/photos/${photoId}`);
      await refreshUser();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete photo');
    }
  };

  const handleLocationUpdate = () => {
    if (!navigator.geolocation) {
      setError('Geolocation not supported');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await api.put('/users/profile', {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
          });
          await refreshUser();
          setSuccess('Location updated');
          setTimeout(() => setSuccess(''), 3000);
        } catch {
          setError('Failed to update location');
        }
      },
      () => setError('Location access denied')
    );
  };

  const tabs = [
    { key: 'info', label: 'Info' },
    { key: 'photos', label: 'Photos' },
    { key: 'prompts', label: 'Prompts' },
    { key: 'traits', label: 'Traits' },
    { key: 'preferences', label: 'Prefs' },
    { key: 'vaccines', label: 'Vaccines' },
  ];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <button style={styles.backBtn} onClick={() => navigate('/profile')}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
        </button>
        <h2 style={styles.title}>Edit Profile</h2>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}{hasChanges && <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: '#fff', marginLeft: 6 }} />}
        </button>
      </div>

      {error && <p className="error-text" style={{ marginBottom: '12px' }}>{error}</p>}
      {success && <p style={{ color: 'var(--success)', fontSize: '13px', marginBottom: '12px' }}>{success}</p>}

      <div style={styles.tabs}>
        {tabs.map((t) => (
          <button
            key={t.key}
            style={{ ...styles.tab, ...(activeTab === t.key ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'info' && (
        <>
          <div style={styles.section}>
            <div className="form-group">
              <label className="form-label">Display name</label>
              <input
                value={displayName}
                onChange={(e) => { setDisplayName(e.target.value); setHasChanges(true); }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Bio</label>
              <textarea
                value={bio}
                onChange={(e) => { setBio(e.target.value.slice(0, 500)); setHasChanges(true); }}
                rows={3}
                placeholder="Tell people about yourself..."
                style={{ resize: 'vertical' }}
              />
              <span style={{ fontSize: '11px', color: bio.length >= 500 ? 'var(--danger)' : bio.length >= 450 ? '#f59e0b' : 'var(--text-muted)' }}>
                {bio.length}/500
              </span>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Identity</h3>
            <div className="form-group">
              <label className="form-label">Gender</label>
              <div className="chip-group">
                {GENDER_OPTIONS.map((g) => (
                  <button
                    key={g}
                    className={`chip ${gender === g ? 'active' : ''}`}
                    onClick={() => { setGender(g); setHasChanges(true); }}
                    type="button"
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Pronouns</label>
              <div className="chip-group">
                {PRONOUN_OPTIONS.map((p) => (
                  <button
                    key={p}
                    className={`chip ${pronouns === p ? 'active' : ''}`}
                    onClick={() => { setPronouns(p); setHasChanges(true); }}
                    type="button"
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Orientation</label>
              <div className="chip-group">
                {ORIENTATION_OPTIONS.map((o) => (
                  <button
                    key={o}
                    className={`chip ${orientation === o ? 'active' : ''}`}
                    onClick={() => { setOrientation(o); setHasChanges(true); }}
                    type="button"
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Interested in</label>
              <div className="chip-group">
                {INTERESTED_IN_OPTIONS.map((i) => (
                  <button
                    key={i}
                    className={`chip ${interestedIn.includes(i) ? 'active' : ''}`}
                    onClick={() => toggleInterestedIn(i)}
                    type="button"
                  >
                    {i}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div style={styles.section}>
            <h3 style={styles.sectionTitle}>Location</h3>
            <button className="btn btn-secondary" onClick={handleLocationUpdate}>
              Update my location
            </button>
          </div>
        </>
      )}

      {activeTab === 'photos' && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>Photos ({photos.length}/6)</h3>
          <div style={styles.photosGrid}>
            {photos.map((photo) => (
              <div key={photo.id} style={styles.photoCell}>
                <img
                  src={`/uploads/${photo.filename}`}
                  alt=""
                  style={styles.photoImg}
                />
                <button
                  style={styles.photoDelete}
                  onClick={() => handleDeletePhoto(photo.id)}
                >
                  ×
                </button>
              </div>
            ))}
            {photos.length < 6 && (
              <div
                style={styles.photoCell}
                onClick={() => document.getElementById('edit-photo-input').click()}
              >
                <span style={styles.addPhoto}>+</span>
                <input
                  id="edit-photo-input"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoUpload}
                  style={{ display: 'none' }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'prompts' && <PromptSelector />}

      {activeTab === 'traits' && <TraitsForm />}

      {activeTab === 'preferences' && <PreferencesForm />}

      {activeTab === 'vaccines' && <VaccinationForm />}
    </div>
  );
}
